// Centralized event layer — one call fans out to GA4 and PostHog exactly once.
//
// Usage:  trackEvent('contact_click', { cta_text: 'Contact Us', cta_location: 'header' })
//
// Page context (page_path / page_title / page_type / environment) is attached
// automatically, undefined + null properties are stripped, and lead-level events
// additionally carry session + first-touch attribution.

import posthog from 'posthog-js';
import { environment } from './env';
import { gtag } from './analytics';
import { getPageType, getVixiProductFromPath } from './analytics-taxonomy';
import { getFirstTouch, getSessionAttribution, getVisitProperties, initAttribution } from './attribution';
import { getConsent } from './consent';

export type AnalyticsEventName =
  // Lead / sales
  | 'contact_click'
  | 'contact_form_start'
  | 'contact_form_submit'
  | 'request_demo_click'
  | 'email_click'
  | 'phone_click'
  // Content / buyer research
  | 'project_impression'
  | 'case_study_view'
  | 'case_study_engaged'
  | 'solution_view'
  | 'vixi_product_view'
  | 'work_filter'
  // Video
  | 'video_start'
  | 'video_progress'
  | 'video_complete'
  // Engagement
  | 'scroll_depth'
  | 'outbound_click'
  | 'linkedin_click'
  | 'cta_click';

export type AnalyticsValue = string | number | boolean | undefined | null;
export type AnalyticsProperties = Record<string, AnalyticsValue>;

/** Events that represent buying intent — attribution is attached so channels can be credited. */
const ATTRIBUTED_EVENTS: ReadonlySet<AnalyticsEventName> = new Set([
  'contact_click',
  'contact_form_start',
  'contact_form_submit',
  'request_demo_click',
  'email_click',
  'phone_click',
  'case_study_view',
  'case_study_engaged',
  'solution_view',
  'vixi_product_view',
]);


function compact(props: AnalyticsProperties): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

function pageContext(): AnalyticsProperties {
  if (typeof window === 'undefined') return {};
  const path = window.location.pathname;
  return {
    page_path: path + window.location.search,
    page_title: document.title,
    page_type: getPageType(path),
    product_family: getVixiProductFromPath(path) ? 'vixi' : undefined,
    product: getVixiProductFromPath(path),
    environment: environment(),
  };
}

/**
 * Send a single analytics event to GA4 and PostHog.
 * Never throws — analytics problems must not break the site.
 */
export function trackEvent(name: AnalyticsEventName, properties: AnalyticsProperties = {}) {
  if (typeof window === 'undefined') return;
  try {
    const merged: AnalyticsProperties = { ...pageContext(), ...properties };
    const consent = getConsent();
    if (ATTRIBUTED_EVENTS.has(name) && (consent.analytics || consent.experience_analytics)) {
      Object.assign(merged, getSessionAttribution(), getFirstTouch(), getVisitProperties());
    }
    const payload = compact(merged);

    // GA4
    gtag('event', name, payload);

    // PostHog
    if (posthog.__loaded) {
      posthog.capture(name, payload);
    }

    if (environment() !== 'production' && (window as { __ANALYTICS_DEBUG__?: boolean }).__ANALYTICS_DEBUG__) {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', name, payload);
    }
  } catch {
    /* swallow — tracking must never break the UI */
  }
}

/**
 * Resolve attribution once at startup and expose first-touch values to PostHog as
 * persistent person properties. Session values stay event-scoped by design.
 */
export function initTracking() {
  if (typeof window === 'undefined') return;
  try {
    const c = getConsent();
    if (!c.analytics && !c.experience_analytics) return;
    const { firstTouch } = initAttribution();
    if (posthog.__loaded) {
      // setOnce semantics: first-touch never overwrites an existing value
      posthog.setPersonProperties(undefined, compact(firstTouch as AnalyticsProperties));
      posthog.register_once(compact(firstTouch as AnalyticsProperties));
    }
  } catch {
    /* ignore */
  }
}
